import { DEFAULT_CARD_BOX, LARGE_CARD_BOX } from "../components/Card";
import { HAND_CARD_SCALE } from "./hand-fan";

/* ------------------------------------------------------------------ *
 * THE POOL'S RULE — پاسور's loose face-up cards laid out to MEASURE (P1 Task 4, W4 / P1-2), pure.
 *
 * The felt leaves the pool a ROOM: below the top seat's name box and the corner chips, above the
 * prompt's row, between the side seats' columns (the table measures it, `PasurTable`'s
 * `usePoolRoom`; a breakpoint cannot see a WebView text zoom, which grows every box around the pool
 * — Task 3's lesson). For `n` cards in that room the rule picks the grid, the scale and the overlap:
 *
 *   columns   never overlap sideways (a tap target is a whole card's width);
 *   rows      may overlap — each later row lies over the one before, leaving at least the card's
 *             INDEX STRIP (`POOL_EXPOSE` of its height: the rank and suit in the corner) exposed, as a
 *             fanned hand does — but only once a plain grid no longer fits;
 *   scale     the largest, never above 1, at which the chosen grid fits, in steps of 0.02 so two
 *             grids a hair apart tie — WEIGHED against the overlap: a covered card counts for
 *             less (its exposed share weighs a quarter of its scale, `worth`), so eight cards go
 *             four by two at 0.88 with nothing covered rather than three rows of full-size cards
 *             showing half of each; then the fewer rows, then the fewer columns. Cards shrink and
 *             overlap as the count grows, like حکم's zoomed rule for its played cards
 *             (`trickCardScale`), from the felt's own room;
 *   growth    and where that is a PLAIN grid at full size, the cards GROW past 1 — the largest
 *             plain grid the room holds, up to your hand's card (`POOL_MAX_SCALE`; the operator's
 *             2026-10-04 rule for حکم's felt: «the same size as in my hand — not bigger»), B3
 *             item 1 (`plainGrid`): nothing the rule left uncovered is ever covered by it;
 *   tilt      the fixed per-slot rotations the pile always had (`POOL_ROT`), at an amplitude that
 *             settles as the pile grows (`poolTilt`), and budgeted: a tilted card's corners stay
 *             inside the room.
 *
 * The NUMBERS the rooms give are the probe's (`pasur-pool.test.ts` pins them): 320×568 under ×1.3
 * leaves 172 × 124 px — sixteen cards four by four at 0.88, the rows 22.7 px apart; the operator's
 * 384×823 and the iPhone 390×844 leave 220 × 272 — up to fifteen cards at the hand's size (B3
 * item 1, operator 2026-10-20: «can the cards on the table be bigger, without overlapping anything
 * else, on every device size?» — they were capped at the 44 × 62 card, 1, until then), sixteen
 * four by four at 1.16. A room
 * nothing fits (256×393@1.5 under ×1.3: a 109 px felt, the room −47 px tall) gets the floor scale
 * in the grid that OVERFLOWS it least (the stack that still fits the room's width), centred, and
 * says `fits: false`: no throw, no vanishing card — that size is Task 8's best-effort list, with
 * these numbers.
 * ------------------------------------------------------------------ */

/** The card the pool draws at scale 1 — `Card`'s default box (44 × 62). */
export const POOL_CARD = DEFAULT_CARD_BOX;
/** Between two cards of a row, and between two rows that do not overlap (px, unscaled). */
export const POOL_GAP = 4;
/** The least of a covered card that stays exposed: its index strip (a share of its height). */
export const POOL_EXPOSE = 0.4;
/** The step the scale moves in — see the header. */
const STEP = 0.02;
const quantize = (s: number): number => Math.floor(s / STEP + 1e-9) * STEP;

/** The LARGEST card the rule draws: your hand's (`Card`'s large box at `HAND_CARD_SCALE`, 57.5 ×
 *  80.5), never bigger — as a step of the scale: 1.28, a 56.3 × 79.4 card (B3 item 1, 2026-10-20). */
export const POOL_MAX_SCALE = quantize(
  Math.min(
    (LARGE_CARD_BOX.w * HAND_CARD_SCALE) / POOL_CARD.w,
    (LARGE_CARD_BOX.h * HAND_CARD_SCALE) / POOL_CARD.h,
  ),
);
/** The smallest card the rule draws — below it the index is no longer a tap target. */
export const POOL_MIN_SCALE = 0.5;
/** The loosest pile's tilt (degrees) — the amplitude of `POOL_ROT`. */
export const POOL_MAX_TILT = 10;
/** Fixed per-slot rotations so the face-up pool reads as a loosely scattered pile (the Stitch
 *  design's rotated table cards) without random jitter — scaled by `poolTilt`. */
export const POOL_ROT = [-8, 7, -4, 10, -6, 5, -10, 8, -3, 9, -7, 4] as const;

/** The tilt's amplitude for a pile of `n`: the full scatter up to four cards, half to eight, a
 *  fifth beyond — a crowded, overlapping pool stays readable when its cards lie nearly square. */
export function poolTilt(n: number): number {
  return n <= 4 ? 1 : n <= 8 ? 0.5 : 0.2;
}

export interface PoolRoom {
  readonly w: number;
  readonly h: number;
}

export interface PoolSlot {
  /** The slot's top-left corner in the room (px) — the card's box before its tilt. */
  readonly x: number;
  readonly y: number;
  /** Degrees, about the card's centre. */
  readonly tilt: number;
}

export interface PoolLayout {
  readonly n: number;
  readonly cols: number;
  readonly rows: number;
  /** The cards' scale (of `POOL_CARD`), a step of 0.02, never above `POOL_MAX_SCALE` nor below
   *  `POOL_MIN_SCALE`. */
  readonly scale: number;
  /** A card's box at that scale. */
  readonly w: number;
  readonly h: number;
  /** From one row's top to the next's — `h + POOL_GAP` when the rows do not overlap. */
  readonly stepY: number;
  /** The block's extent (the slots' boxes, tilt excluded). */
  readonly width: number;
  readonly height: number;
  /** False when even the floor scale does not fit the room: the block is centred and overflows. */
  readonly fits: boolean;
  readonly slots: readonly PoolSlot[];
}

/** How far a card's tilted corners reach past its box, per side, at scale 1 (x and y). */
function tiltPad(deg: number): { x: number; y: number } {
  const a = (deg * Math.PI) / 180;
  const { w, h } = POOL_CARD;
  return {
    x: Math.max(0, (w * Math.cos(a) + h * Math.sin(a) - w) / 2),
    y: Math.max(0, (h * Math.cos(a) + w * Math.sin(a) - h) / 2),
  };
}

interface Candidate {
  cols: number;
  rows: number;
  scale: number;
  stepY: number;
  /** The exposed share of a covered card (≥ 1 when nothing is covered). */
  exposure: number;
  /** The grid's worth: its scale, weighed by what a covered card still shows (see the header). */
  worth: number;
  /** How far past the room it reaches (px, both axes) — 0 when it fits. */
  overflow: number;
}

/** A grid's worth: its scale, a covered card's exposed share weighing a quarter of it. */
const worthOf = (scale: number, exposure: number): number =>
  scale * (exposure >= 1 ? 1 : 0.75 + 0.25 * exposure);

/** The grid chosen for `n` cards in `room`, its scale never above `cap` — see the header. */
function choose(n: number, room: PoolRoom, cap: number): { best: Candidate; fits: boolean } {
  const { w: CW, h: CH } = POOL_CARD;
  const pad = tiltPad(POOL_MAX_TILT * poolTilt(n));
  let best: Candidate | null = null;
  let bestFits = false;
  for (let cols = 1; cols <= n; cols++) {
    const rows = Math.ceil(n / cols);
    const byWidth = (room.w - (cols - 1) * POOL_GAP) / (cols * CW + 2 * pad.x);
    const byHeight = room.h / ((rows - 1) * POOL_EXPOSE * CH + CH + 2 * pad.y);
    const raw = Math.min(1, byWidth, byHeight);
    const fits = raw >= POOL_MIN_SCALE;
    const scale = fits ? Math.min(cap, quantize(raw)) : POOL_MIN_SCALE;
    const h = CH * scale;
    const stepY =
      rows === 1
        ? 0
        : Math.max(
            POOL_EXPOSE * h,
            Math.min(h + POOL_GAP, (room.h - h - 2 * pad.y * scale) / (rows - 1)),
          );
    const exposure = rows === 1 ? 1 : stepY / h;
    const w = CW * scale;
    const overflow =
      Math.max(0, cols * w + (cols - 1) * POOL_GAP + 2 * pad.x * scale - room.w) +
      Math.max(0, (rows - 1) * stepY + h + 2 * pad.y * scale - room.h);
    const c: Candidate = {
      cols,
      rows,
      scale,
      stepY,
      exposure,
      worth: worthOf(scale, exposure),
      overflow,
    };
    // a fitting grid beats any that does not; among fitting grids the worthiest wins, then the
    // fewer rows (then columns); among grids that do not fit, the one that overflows least
    const better =
      best === null ||
      (fits && !bestFits) ||
      (fits === bestFits &&
        (fits
          ? c.worth > best.worth + 1e-9 ||
            (Math.abs(c.worth - best.worth) < 1e-9 && c.rows < best.rows)
          : c.overflow < best.overflow - 1e-9 ||
            (Math.abs(c.overflow - best.overflow) < 1e-9 && c.worth > best.worth + 1e-9)));
    if (better) {
      best = c;
      bestFits = fits;
    }
  }
  return { best: best!, fits: bestFits };
}

/**
 * THE GROWTH PAST 1 (B3 item 1, operator 2026-10-20: «can the cards on the table be bigger,
 * without overlapping anything else, on every device size?»): where the rule above lays the pile
 * out at full size in a PLAIN grid (no row over another), it may be larger — the largest PLAIN grid
 * the room holds, up to `cap` (your hand's card, `POOL_MAX_SCALE`), in steps of 0.02, the fewer
 * rows on a tie. It never covers a card the rule left uncovered, and wherever the rule shrank or
 * overlapped the pile (a narrow screen, a big text zoom, a crowded pool) nothing changes.
 */
function plainGrid(
  n: number,
  room: PoolRoom,
  cap: number,
): { cols: number; rows: number; scale: number } {
  const { w: CW, h: CH } = POOL_CARD;
  const pad = tiltPad(POOL_MAX_TILT * poolTilt(n));
  let best: { cols: number; rows: number; scale: number } | null = null;
  for (let cols = 1; cols <= n; cols++) {
    const rows = Math.ceil(n / cols);
    const byWidth = (room.w - (cols - 1) * POOL_GAP) / (cols * CW + 2 * pad.x);
    const byHeight = (room.h - (rows - 1) * POOL_GAP) / (rows * CH + 2 * pad.y);
    const scale = Math.min(cap, quantize(Math.min(byWidth, byHeight)));
    if (
      best === null ||
      scale > best.scale + 1e-9 ||
      (Math.abs(scale - best.scale) < 1e-9 && rows < best.rows)
    )
      best = { cols, rows, scale };
  }
  return best!;
}

/**
 * Lay `n` cards out in `room` — see the header for the rule. MONOTONE in `n`: a card never grows
 * when another lands, so the pile only ever settles as it fills (the scale chosen for `n` is capped
 * by the scale chosen for `n − 1`, all the way down) — a ninth card could otherwise make eight
 * small, unoverlapped cards jump to full size in three overlapping rows.
 */
export function poolLayout(n: number, room: PoolRoom): PoolLayout {
  const { w: CW, h: CH } = POOL_CARD;
  if (n <= 0) {
    return {
      n: 0,
      cols: 0,
      rows: 0,
      scale: 1,
      w: CW,
      h: CH,
      stepY: 0,
      width: 0,
      height: 0,
      fits: true,
      slots: [],
    };
  }
  let cap = 1;
  // the GROWTH past 1 (see `plainGrid`) has its own ceiling, carried down the counts the same way
  let growCap = POOL_MAX_SCALE;
  let chosen = choose(1, room, cap);
  let grown: { cols: number; rows: number; scale: number } | null = null;
  for (let m = 1; m <= n; m++) {
    chosen = choose(m, room, cap);
    cap = chosen.best.scale;
    const b = chosen.best;
    const plain = b.rows === 1 || b.stepY >= CH * b.scale + POOL_GAP - 1e-9;
    if (chosen.fits && plain && b.scale >= 1 - 1e-9) {
      const g = plainGrid(m, room, growCap);
      growCap = Math.max(1, g.scale);
      grown = g.scale > b.scale + 1e-9 ? g : null;
    } else {
      growCap = Math.min(growCap, b.scale);
      grown = null;
    }
  }
  const { cols, rows, scale, stepY } = grown
    ? { ...grown, stepY: grown.rows === 1 ? 0 : CH * grown.scale + POOL_GAP }
    : chosen.best;
  const w = CW * scale;
  const h = CH * scale;
  const width = cols * w + (cols - 1) * POOL_GAP;
  const height = (rows - 1) * stepY + h;
  const y0 = (room.h - height) / 2;
  const amp = poolTilt(n);
  const slots: PoolSlot[] = [];
  for (let i = 0; i < n; i++) {
    const row = Math.floor(i / cols);
    const col = i % cols;
    const inRow = Math.min(cols, n - row * cols);
    const rowWidth = inRow * w + (inRow - 1) * POOL_GAP;
    slots.push({
      x: (room.w - rowWidth) / 2 + col * (w + POOL_GAP),
      y: y0 + row * stepY,
      tilt: POOL_ROT[i % POOL_ROT.length]! * amp,
    });
  }
  return { n, cols, rows, scale, w, h, stepY, width, height, fits: chosen.fits, slots };
}
